<template>
  <div
    class="base-item pb-3 position-relative"
    :class="{ 'item-active': activeHighlightIndex === index }"
    @click="onItemClick"
  >
    <eg-colored-dot-box
      v-if="item.color"
      :color="item.color"
      :background-color="item.backgroundColor"
      class="mt-1"
    />
    <!-- Delete button for grammar items (icon only) -->
    <eg-button
      v-if="!specialForCopyPaste && $listeners.delete && isRealItem && !title"
      size="sm"
      type="secondary"
      class="delete-button-absolute"
      :icon="'delete'"
      @click.stop="deleteItem"
    />
    <div class="w-100 overflow-auto-wrapper">
      <div
        v-if="title"
        class="title d-flex align-items-center justify-content-between mb-2"
      >
        <!-- h3 for semantic, but should looks like h5 -->
        <h3
          v-b-tooltip.hover
          class="h5 text-truncate heading-xs"
          :title="title"
        >
          <slot name="title">
            <span>{{ title }}</span>
          </slot>
        </h3>

        <div
          class="actions justify-content-between flex-shrink-0"
          :class="{
            active:
              isEdit ||
              (isRealItem ? activeHighlightIndex === index : isClicked),
          }"
        >
          <div v-if="isEdit && isRealItem && item?.criteria?.maxGrade">
            <div class="d-flex align-items-center mr-2">
              <div class="mr-2 minimal-grade-criteria">{{ newGrade }}</div>
              <grade-slider
                v-model="newGrade"
                :min="0"
                :max="item.criteria.maxGrade"
                :step="0.1"
                class="mb-1"
              />
              <div class="ml-2">{{ item.criteria.maxGrade }}</div>
              <eg-b-form-input
                :value="newGrade"
                class="ml-2 grade-input"
                size="sm"
                type="number"
                min="0"
                :max="item.criteria.maxGrade"
                step="0.1"
                @update="updateGrade"
              ></eg-b-form-input>
            </div>
          </div>

          <div
            v-if="!specialForCopyPaste"
            class="d-flex no-print"
            :class="{ 'ml-auto': !(isEdit && isRealItem) }"
          >
            <eg-button
              v-if="$listeners.update"
              type="secondary"
              size="sm"
              class="ml-auto mr-2"
              :icon="isEdit ? 'close' : 'pen-edit'"
              @click="() => (isEdit ? cancel() : edit())"
            >
              {{ isEdit ? $t("common.cancel") : $t("common.edit") }}
            </eg-button>
            <eg-button
              v-if="(index < 0 && isEdit) || ($listeners.delete && isRealItem)"
              size="sm"
              :type="isEdit ? 'primary' : 'secondary'"
              :icon="isEdit ? 'check' : 'delete'"
              :is-loading="saveLoading"
              @click.stop="() => (isEdit ? save() : deleteItem())"
            >
              {{ isEdit ? $t("common.save") : $t("common.delete") }}
            </eg-button>
          </div>
        </div>
      </div>

      <div class="content-container">
        <div v-if="isEdit">
          <eg-b-form-textarea
            v-model="newAnalysisText"
            class="paragraph-s editable-textarea"
            placeholder="Enter analysis..."
            rows="2"
            max-rows="4"
          />
        </div>

        <slot v-else name="content" :item="item" :index="index">
          <div class="d-flex paragraph-s">
            <eg-svg-text
              v-if="isRealItem && showIndex"
              class="index flex-shrink-0 mr-1"
              :text="index + 1"
              :size="10"
            />
            <div
              v-if="typeof item.analysis === 'string'"
              v-html="item.analysis"
            />
            <component :is="item.analysis" v-else></component>
          </div>
        </slot>

        <div v-if="item[nextStepsKey]">
          <!-- TODO: bad practice to compare against the title, change this -->
          <next-steps-box
            :title="nextStepTitle"
            :text="item[nextStepsKey]"
            :is-list="item.isList"
          >
            <template v-if="isEdit" #content>
              <eg-b-form-textarea
                v-model="newNextText"
                class="mt-2 editable-textarea paragraph-s"
                placeholder="Enter next steps..."
                rows="2"
                max-rows="4"
              />
            </template>
          </next-steps-box>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EgSvgText from "@/components/global/svgText/eg_svg_text.vue";
import NextStepsBox from "@/components/essay/essay/v3/next_steps_box.vue";
import EgButton from "@/components/global/eg/buttons/eg_button.vue";
import EgColoredDotBox from "@/components/global/eg/eg_colored_dot_box.vue";
import GradeSlider from "@/components/essay/essay/v3/grade_slider.vue";
import deleteConfirmationModalMixin from "@/mixins/deleteConfirmationModal";

export default {
  components: {
    NextStepsBox,
    EgSvgText,
    EgButton,
    EgColoredDotBox,
    GradeSlider,
  },

  // this.showDeleteConfirmationModal
  mixins: [deleteConfirmationModalMixin],

  inject: ["specialForCopyPaste"],

  props: {
    item: {
      type: Object,
      required: true,
    },

    activeHighlightIndex: {
      type: Number,
      default: -1,
    },

    index: {
      type: Number,
      required: true,
    },

    formatTitle: {
      type: Function,
      default: (item) => item.title || "",
    },

    nextStepsKey: {
      type: String,
      default: "suggestions",
    },

    nextStepTitle: {
      type: String,
      default: "Suggestions:",
    },

    showIndex: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isEdit: false,
      newAnalysisText: "",
      newNextText: "",
      newGrade: 0,
      isClicked: false,
      saveLoading: false,
    };
  },

  computed: {
    isRealItem() {
      return this.index >= 0;
    },

    title() {
      return this.formatTitle(this.item);
    },
  },

  methods: {
    onItemClick() {
      if (this.isEdit) return;

      !this.isRealItem && (this.isClicked = !this.isClicked);
      this.$emit("updateActiveHighlightIndex", this.index);
    },

    updateGrade(value) {
      if (value > this.item.criteria.maxGrade) {
        // need to rerender every time to get the correct value
        // b-form-input has local value, that meens we need to update props value
        // first set to 0, then set to the max grade
        this.newGrade = 0;
        this.$nextTick(() => {
          this.newGrade = this.item.criteria.maxGrade;
        });
      } else if (value < 0) {
        this.newGrade = 0;
      } else {
        this.newGrade = Number(value).toFixed(1);
      }
    },

    edit() {
      this.isEdit = true;
      this.newAnalysisText = this.item.analysis;
      this.newNextText = this.item.suggestions;
      this.newGrade = Number(this.item.grade).toFixed(1);
    },

    cancel() {
      this.isEdit = false;
      this.newAnalysisText = "";
      this.newNextText = "";
      this.newGrade = "";
    },

    save() {
      this.saveLoading = true;
      const data = {
        itemId: this.item.id,
        analysis: this.newAnalysisText,
        suggestions: this.newNextText,
        grade: Number(this.newGrade),
      };

      if (this.newGrade) {
        data.grade = Number(this.newGrade);
      }

      this.$emit("update", {
        data,
        resolve: () => {
          this.saveLoading = false;
          this.cancel();
        },
      });
    },

    async deleteItem() {
      const canDelete = await this.showDeleteConfirmationModal({
        title: "Are you sure you want to delete this section?",
        message:
          "This action cannot be undone, but you can regrade the submission to regenerate the feedback.",
      });

      if (!canDelete) return;

      this.$emit("delete", { itemId: this.item.id });
    },
  },
};
</script>

<style lang="scss" scoped>
.title {
  height: 31px;

  .h5 {
    margin: auto 0;
    color: var(--color-text-neutral-on-white);
  }
}

.base-item.item-active,
.item-active {
  border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-hover, #f6f2ff);
}

.content {
  cursor: pointer;
}

.index {
  color: var(--Text-Secondary, #6c757d);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
}

.base-item {
  display: flex;
  padding: 1rem 1.5rem;
  margin: 0 1.5rem;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  &.bordered {
    border: 1px solid var(--color-stroke-neutral-default);
  }
  &.bordered-first-item {
    border-top-right-radius: 0.5rem;
  }
  &.border-bottom {
    border-bottom: 1px solid #dfdfdf;
    .h5 {
      font-size: var(--typography-font-size-fs-300);
    }
  }
}

.grade-input {
  max-width: 70px;
}

.minimal-grade-criteria {
  min-width: 22px;
}
.editable-textarea {
  padding: 0.5rem;
  border: 1px solid var(--color-stroke-neutral-default);
  color: var(--color-text-neutral-on-white);
  overflow-y: auto !important;
}
.actions {
  display: flex;
  justify-content: flex-end;
  opacity: 0;

  &:hover,
  &.active {
    opacity: 1;
  }
}
.minimal-grade-criteria {
  min-width: 22px;
}
.base-item:hover .actions {
  opacity: 1;
}

.delete-button-absolute {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0;
  transition: opacity 0.2s ease, right 0.2s ease;
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.8) !important;
}

.base-item:hover .delete-button-absolute {
  opacity: 1;
}

.overflow-auto-wrapper {
  overflow: auto;
  padding: 2px; // Prevent focus border/box-shadow clipping
  margin: -2px; // Compensate for padding to maintain layout
}
</style>
